import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  ThemeProvider,
  createTheme,
  useScrollTrigger,
  useTheme,
} from "@mui/material";
// import { initAuthSocket, authSocket } from "../../utils/socket";

import { MainSnackbar } from "../../components/snackbars";
import TopRightMenu from "./TopRightMenu";

import {
  // adminActions,
  useSelector,
  authSelectors,
  redirectToLogin,
  uiSelectors,
  // wizardActions,
} from "../../state";

import { useStyles } from "./AdminLayout.styles";
import reliLogo from "../../assets/img/reliexchange_logo.png";
import reliLogoMark from "../../assets/img/Reli-logomark.svg";
import AdminSearchBox from "../../components/inputs/AdminSearchBox";
import MobileSearchView from "../../components/inputs/MobileSearchView";
import AgencySelectionBox from "../../components/inputs/AgencySelectionBox";
import { AdminSidebar, AdminBottomNav } from "../../components/admin";
import { resolveActivePage } from "../../pages/admin";
import { useMobile } from "../../themes";
import { LaunchIcon, SearchIcon } from "../../components/icons";
import { history } from "../../lib";
import ModalNewAgency from "../../components/modals/ModalNewAgency";
import ModalCarrier from "../../components/modals/ModalCarrier";
import ModalBind from "../../components/modals/ModalBind";
import ModalAgent from "../../components/modals/ModalAgent";
import ModalCoverage from "../../components/modals/ModalActivity";
import ModalAgentTransfer from "../../components/modals/ModalAgentTransfer";
import ModalTask from "../../components/modals/ModalTask";
import ModalUpload from "../../components/modals/ModalUpload";
import ModalAssignReferrer from "../../components/modals/ModalAssignReferrer";

/**
 * Builds an admin-only theme that applies a fixed dark navy color to all
 * heading variants (h1-h6). Done as a nested ThemeProvider so the public
 * wizard (e.g. `/auto/wizard/start`) keeps its existing heading colors.
 *
 * NOTE: The color is hard-coded here rather than read from
 * `palette.primary.dark` because agency-level theme overrides (loaded via
 * `getTheme(agencyData.theme)` in CustomThemeProvider) shallow-replace the
 * entire `palette` object, which can change `primary.dark` to a value we
 * don't want for admin headings.
 */
const ADMIN_HEADING_COLOR = "#000c3b";

const useAdminTheme = () => {
  const outerTheme = useTheme();
  return useMemo(() => {
    return createTheme(outerTheme, {
      typography: {
        h1: { color: ADMIN_HEADING_COLOR },
        h2: { color: ADMIN_HEADING_COLOR },
        h3: { color: ADMIN_HEADING_COLOR },
        h4: { color: ADMIN_HEADING_COLOR },
        h5: { color: ADMIN_HEADING_COLOR },
        h6: { color: ADMIN_HEADING_COLOR },
      },
    });
  }, [outerTheme]);
};

export const AdminLayout = ({ children }) => {
  const classes = useStyles();
  const adminTheme = useAdminTheme();
  const location = useLocation();
  // Per-page layout opt-out: pages may set `fullBleed: true` in their route
  // config (see `Page.fullBleed` and `resolveActivePage`) to drop the default
  // 25px content gutter so they render flush against the side rail.
  const activePage = resolveActivePage(location.pathname);
  const isFullBleed = !!activePage?.fullBleed;

  // const token = useSelector(authSelectors.token);
  const activeAgencyId = useSelector(authSelectors.activeAgencyId);
  const activeRole = useSelector(authSelectors.activeRole);
  const userAgencies = useSelector(authSelectors.user_agency);
  const affiliateToken = useSelector(authSelectors.affiliateToken);
  const isUserLoggedIn = useSelector(authSelectors.isUserLoggedIn);

  const { name } = useSelector(uiSelectors.agencyData);
  const isMobile = useMobile();
  const isTablet = useMobile("md");
  const isCompact = isMobile || isTablet;
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const notProductOwner = activeRole !== "referral_product_owner";
  const isAffiliateAgent = activeRole === "affiliate_agent";
  const activeAgency = userAgencies.find(
    (ua) => ua.agency_id === activeAgencyId,
  );
  const domain = activeAgency?.agency.domain;
  const token = activeAgency?.referral_token;

  useEffect(() => {
    document.title = `${name} | The easy way to buy auto and home insurance online`;
  }, [name]);
  const urlParam = affiliateToken
    ? `affiliate_token=${affiliateToken}`
    : `referral_token=${token}`;
  const SiteLink = () => {
    return (
      <Button
        variant="contained"
        color="secondary"
        size="small"
        href={`https://${domain}?${urlParam}`}
        target="_blank"
        rel="noreferrer"
        endIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: "none",
          fontSize: 13,
          fontWeight: 500,
          // Match the AdminSearchBox outlined input which lands at 40px
          // tall (its mobile rule is explicit; desktop comes from MUI's
          // default Autocomplete inputRoot padding). Aligning here keeps
          // the top-nav controls on a single visual baseline.
          height: 40,
          px: "12px",
          // Keep the label on a single line and prevent the flex parent
          // from shrinking the button at narrow desktop widths.
          whiteSpace: "nowrap",
          flexShrink: 0,
          "& .MuiButton-endIcon": {
            marginLeft: "6px",
          },
        }}
      >
        View site
      </Button>
    );
  };
  //currently not used
  // useEffect(() => {
  //   if (token) {
  //     initAuthSocket(token);
  //   }
  //   if (token && authSocket) {
  //     if (!activeRole || !agencyId) {
  //       if (activeRole !== "referral_product_owner") {
  //         console.warn("active role or active agency id is not defined");
  //       }
  //       return;
  //     }
  //     authSocket.on("admin_message", (e) => {
  //       switch (e.action) {
  //         case "getSelectedQuote":
  //           dispatch(wizardActions.getQuoteData(e.req_txn_id));
  //           break;
  //         case "showPmtPopup":
  //           dispatch(wizardActions.showPmtPopup(e.showPaymentPopup));
  //           break;
  //         case "updatePmt":
  //           //This is not used, payment is pulled from BE instead
  //           dispatch(adminActions.getPmtInfo(e.req_uid, activeRole, agencyId));
  //           break;
  //         default:
  //           console.error("not handled");
  //       }
  //     });
  //   } else {
  //     console.warn("not listening yet");
  //   }
  // }, [dispatch, token, authSocket, activeRole, agencyId]);

  useEffect(() => {
    if (!isUserLoggedIn) {
      redirectToLogin();
    }
  }, [isUserLoggedIn]);

  // MobileSearchView owns its own Esc handler and body-scroll lock.

  const showMobileSearchTrigger =
    isCompact && notProductOwner && !isAffiliateAgent;
  const showMobileAgencyRow = isCompact && userAgencies.length > 1;
  // Collapse the second-row agency selector once the page has scrolled
  // a few pixels. `disableHysteresis` makes this a pure threshold check
  // so the row reappears as soon as the user returns to the top — there
  // is no "scroll-up to reveal" complexity to surprise the user.
  const isScrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 16,
  });
  const agencyRowExpanded = showMobileAgencyRow && !isScrolled;

  return (
    <ThemeProvider theme={adminTheme}>
      <div
        className={clsx(classes.page, {
          [classes.pageWithMobileAgency]: showMobileAgencyRow,
          [classes.pageWithAgencySubheader]: agencyRowExpanded,
        })}
      >
        <header
          className={clsx(classes.header, {
            [classes.headerWithAgency]: agencyRowExpanded,
          })}
        >
          <div className={classes.headerTopRow}>
            {/* On mobile/tablet the hamburger is replaced by the
                "More" tab in <AdminBottomNav>, which toggles the same
                `adminMenuOpen` state. Keeping a single control avoids
                having two UI affordances do the exact same thing. */}
            {/* Product owners do not have access to `/admin/dashboard`
                (see `dashboard.allowedRoles` in `pages/admin/index.ts`),
                so we route them to `/admin/referrals` — the landing
                page they are authorized to view. */}
            <div
              className={classes.topLeft}
              role="button"
              tabIndex={0}
              aria-label={
                notProductOwner ? "Go to dashboard" : "Go to referrals"
              }
              onClick={() =>
                history.push(
                  notProductOwner ? "/admin/dashboard" : "/admin/referrals",
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  history.push(
                    notProductOwner ? "/admin/dashboard" : "/admin/referrals",
                  );
                }
              }}
            >
              <img
                alt="Company logo"
                src={isCompact ? reliLogoMark : reliLogo}
                className={isCompact ? classes.logoMark : classes.logo}
              />
            </div>
            <div className={classes.topCenter}>
              {!isCompact && (
                <>
                  <div className={classes.agencyWrapper}>
                    {userAgencies.length > 1 && (
                      <AgencySelectionBox key="desktop" />
                    )}
                  </div>
                  {notProductOwner && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                      }}
                    >
                      <SiteLink />
                      {!isAffiliateAgent ? <AdminSearchBox /> : null}
                    </Box>
                  )}
                </>
              )}
            </div>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {showMobileSearchTrigger && (
                <IconButton
                  aria-label="Open search"
                  onClick={() => setMobileSearchOpen(true)}
                  size="large"
                  className={classes.mobileSearchTrigger}
                >
                  <SearchIcon />
                </IconButton>
              )}
              <TopRightMenu />
            </Box>
          </div>
          {showMobileAgencyRow && (
            <div
              className={clsx(classes.headerAgencyRow, {
                [classes.headerAgencyRowCollapsed]: !agencyRowExpanded,
              })}
            >
              <AgencySelectionBox key="mobile" />
            </div>
          )}
        </header>
        {isCompact && mobileSearchOpen && (
          <MobileSearchView onClose={() => setMobileSearchOpen(false)} />
        )}
        {notProductOwner && <AdminSidebar />}
        <div
          className={clsx(classes.content, {
            [classes.contentFullBleed]: isFullBleed,
          })}
        >
          {children}
        </div>
        {notProductOwner && <AdminBottomNav />}
        <MainSnackbar />
        <ModalNewAgency />
        <ModalCarrier />
        <ModalAgent />
        <ModalBind />
        <ModalCoverage />
        <ModalAgentTransfer />
        <ModalTask />
        <ModalUpload />
        <ModalAssignReferrer />
      </div>
    </ThemeProvider>
  );
};
